import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  forwardRef,
  ViewChildren,
  ViewChild,
  ElementRef,
  OnChanges,
  HostListener,
  ChangeDetectorRef,
  AfterViewInit,
  Injector,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { ICNInputControlState } from "./models/bfx-interface-controls.enums";
import { ICTimeControlModel, ICSuspendModel } from "./models/bfx-interface-controls.models";

export const SUSPENSION_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxSuspendControlComponent),
  multi: true,
};
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OENLockedState } from "../../data/enums/oe-locked-state.enum";
import {
  BfxTimeInputComponent,
  INPUT_TIME_SHOWSECONDS,
  INPUT_TIME_PATTERN,
  INPUT_TIME_MASK,
  INPUT_TIME_DEFAULT,
  NOSECS_MASK,
  NOSECS_VALIDATE_TIME_PATTERN,
  NOSECS_DEFAULT,
} from "./bfx-time-input.component";
import { Checkbox } from "@bfx/primeng/checkbox";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";

@Component({
  selector: "bfx-suspend-control",
  templateUrl: "./html/bfx-suspend-control.html",
  providers: [
    SUSPENSION_CONTROL_VALUE_ACCESSOR,
    { provide: INPUT_TIME_SHOWSECONDS, useValue: false },
    { provide: INPUT_TIME_PATTERN, useValue: NOSECS_VALIDATE_TIME_PATTERN },
    { provide: INPUT_TIME_MASK, useValue: NOSECS_MASK },
    { provide: INPUT_TIME_DEFAULT, useValue: NOSECS_DEFAULT },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxSuspendControlComponent
  implements AfterViewInit, OnInit, OnChanges, ControlValueAccessor
{
  @Input("labelTo") input_labelTo: string;
  @Input("labelFrom") input_labelFrom: string;

  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  public data: any = {
    value: null, // BfxOrderEntrySuspendModel
    lockState: OENLockedState.UNLOCKED,
    locked: false,
    state: ICNInputControlState.INACTIVE,
    from: {
      label: "Suspend From",
      value: null, //BfxOrderEntryTimeControlModel
      externalState: null,
      lockState: OENLockedState.UNLOCKED,
    },
    to: {
      label: "Suspend To",
      value: null, // BfxOrderEntryTimeControlModel
      externalState: null,
      lockState: OENLockedState.UNLOCKED,
    },
    checkbox: {
      label: "Suspend From/To",
      value: false,
      lockState: OENLockedState.UNLOCKED,
      locked: false,
    },
  };

  @ViewChild("inputSuspendFrom", { static: true })
  suspendFromControl: BfxTimeInputComponent;
  @ViewChild("inputSuspendTo", { static: true })
  suspendToControl: BfxTimeInputComponent;
  @ViewChild("chkBox", { static: true }) suspendCheckbox: Checkbox;
  @ViewChild("inputs", { static: true }) inputs: ElementRef;

  // Main external change state
  private _externalState: ICNInputControlState;

  @Input() get externalState() {
    return this._externalState;
  }

  set externalState(value: any) {
    this.cd.markForCheck();
    this._externalState = value;
    this.data.from.externalState = value;
    this.data.to.externalState = value;
  }

  // From external change state
  @Output() externalStateChangeFrom: EventEmitter<ICNInputControlState | null> =
    new EventEmitter<ICNInputControlState | null>();

  get externalStateFrom() {
    return this.data.from.externalState;
  }

  @Input()
  set externalStateFrom(value: any) {
    this.cd.markForCheck();
    this.data.from.externalState = value;
    this.externalStateChangeFrom.emit(value);
  }

  @Output() externalStateChangeTo: EventEmitter<ICNInputControlState | null> =
    new EventEmitter<ICNInputControlState | null>();

  get externalStateTo() {
    return this.data.to.externalState;
  }

  @Input()
  set externalStateTo(value: any) {
    this.cd.markForCheck();
    this.data.to.externalState = value;
    this.externalStateChangeTo.emit(value);
  }

  _disabled: boolean;

  get disabled() {
    return this._disabled || this.data.locked;
  }

  @Input()
  set disabled(value) {
    this.cd.markForCheck();
    this._disabled = value;
  }

  ///////////////////

  constructor(
    injector: Injector,
    private elementRef: ElementRef,
    public inputDirective: BfxInputDirective,
    public cd: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    if (this.inputs != null) {
      this.inputs.nativeElement.style.display = this.data.checkbox.value ? "flex" : "none";
    }
  }

  ngOnInit() {
    this.initialize();
  }

  ngOnChanges() {
    this.initialize();
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.cd.markForCheck();

    // set labels
    if (
      this.input_labelFrom != null &&
      typeof this.input_labelFrom === "string" &&
      this.input_labelFrom.length > 0
    ) {
      this.data.from.label = this.input_labelFrom;
    }
    if (
      this.input_labelTo != null &&
      typeof this.input_labelTo === "string" &&
      this.input_labelTo.length > 0
    ) {
      this.data.to.label = this.input_labelTo;
    }
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  writeValue(value: any): void {
    this.data.value = value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  public onFocus(event: any): void {
    this.onModelTouched();
  }

  public onBlur(event: any): void {
    this.onChange(this.data.value);
    this.blur.emit(event);
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  /**********************************************************************/
  /* EVENT HANDLERS */

  /* CHECKBOX */
  public onSuspendCheckboxChange(): void {
    if (this.data.checkbox.value) {
      // this.data.state = ICNInputControlState.ACTIVE;
      setTimeout(() => {
        this.suspendFromControl.focusInput();
      }, 0);
    } else {
      this.data.state = ICNInputControlState.INACTIVE;
    }
    this.updateValue(false);
  }

  public onCheckboxFocus(event: FocusEvent): void {
    // this.data.state = ICNInputControlState.ACTIVE;
    this.suspendCheckbox.focused = true;
  }

  public onCheckboxBlur(event: FocusEvent): void {
    this.data.state = ICNInputControlState.INACTIVE;
    this.suspendCheckbox.focused = false;
  }

  /**
   * Works ok on desktop
   * Could be improved, since on desktop its also triggered on main window (if order entry is open),
   * but due to the focus guard has no effect
   */
  //@HostListener('document:keydown.space', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (this.suspendCheckbox.focused) {
      if (event.keyCode === 32) {
        this.data.checkbox.value = !this.data.checkbox.value;
        event.preventDefault();
      }
    }
  }

  /* SUSPEND FROM */
  public onSuspendFromChange($event: any): void {
    this.updateValue();
  }

  public onSuspendFromBlur($event): void {
    if (this.suspendToControl != null) {
      this.suspendToControl.validate();
    }
    this.updateValue();
    this.onBlur($event);
  }

  /* SUSPEND TO */
  public onSuspendToChange(): void {
    this.updateValue();
  }

  public onSuspendToBlur($event): void {
    if (this.suspendFromControl != null) {
      this.suspendFromControl.validate();
    }
    this.updateValue();
    this.onBlur($event);
  }

  /**********************************************************************/
  /* Model Update & Validation*/

  public updateValue(updateState: boolean = true): void {
    if (this.data.value == null) {
      this.data.value = new ICSuspendModel();
    }

    if (this.validateSuspend(updateState)) {
      this.data.value.suspendFrom = this.data.from.value;
      this.data.value.suspendTo = this.data.to.value;
      this.data.value.activated = this.data.checkbox.value;
    } else {
      this.data.value.suspendFrom = null;
      this.data.value.suspendTo = null;
      this.data.value.activated = this.data.checkbox.value;
    }
    this.onChange(this.data.value);
    this.cd.markForCheck();
  }

  protected validateSuspend(updateState: boolean = true): boolean {
    this.cd.markForCheck();

    if (this.data.checkbox.value === true) {
      if (updateState) {
        this.data.externalState = null;
      }
      // if null -> reset
      if (this.data.from.value == null) {
        this.resetSuspendFrom();
      }
      if (this.data.to.value == null) {
        this.resetSuspendTo();
      }

      if (
        this.data.from.value != null &&
        this.data.to.value != null &&
        this.data.from.value.timezoneValue != null &&
        this.data.from.value.timezoneValue.value != null &&
        this.data.from.value.timeValue != null &&
        this.data.to.value.timezoneValue != null &&
        this.data.to.value.timezoneValue.value != null &&
        this.data.to.value.timeValue != null
      ) {
        if (
          this.suspendFromControl.state === ICNInputControlState.INVALID ||
          this.suspendToControl.state === ICNInputControlState.INVALID
        ) {
          if (updateState) {
            this.data.state = ICNInputControlState.INVALID;
          }
          return false;
        } else if (
          this.data.from.value.timezoneValue.value === this.data.to.value.timezoneValue.value &&
          this.data.from.value.timeValue === this.data.to.value.timeValue
        ) {
          if (updateState) {
            this.data.state = ICNInputControlState.INVALID;
          }
          return false;
        } else {
          if (updateState) this.data.state = ICNInputControlState.VALID;
          return true;
        }
        //return true; /// assigned to true in order to activate external validation
      } else {
        if (updateState) this.data.state = ICNInputControlState.INVALID;
        return false;
      }
    } else {
      if (updateState) this.data.state = ICNInputControlState.INACTIVE;
      return false;
    }
  }

  /**********************************************************************/
  /* LOAD */
  public loadSuspendValue(valueModel: ICSuspendModel): void {
    if (valueModel != null) {
      this.data.value = valueModel;
      this.suspendFromControl.loadInputControl(valueModel.suspendFrom);
      this.suspendToControl.loadInputControl(valueModel.suspendTo);

      if (this.data.checkbox.value !== valueModel.activated) {
        this.data.checkbox.value = valueModel.activated;
        this.onSuspendCheckboxChange();
      }
      this.updateValue();
    } else {
      this.data.value.suspendFrom = null;
      this.data.value.suspendTo = null;
      this.data.value.activated = this.data.checkbox.value;
    }
    this.onChange(this.data.value);
  }

  /**********************************************************************/
  /* RESET */

  public resetInputs() {
    this.data.value = null;
    this.data.lockState = OENLockedState.UNLOCKED;
    this.data.locked = false;
    this.data.state = ICNInputControlState.INACTIVE;
    // checkbox
    this.data.checkbox.value = false;
    this.data.checkbox.lockState = OENLockedState.UNLOCKED;
    this.data.checkbox.locked = false;
    // time inputs
    this.resetSuspendFrom();
    this.resetSuspendTo();
    // update locking
    this.cd.markForCheck();
  }

  public resetSuspendFrom(): void {
    // this.data.from.value = null;
    this.data.from.externalState = null;
    this.data.from.lockedState = OENLockedState.UNLOCKED;
    this.suspendFromControl.resetInputControl();
    this.updateLocking();
  }

  public resetSuspendTo(): void {
    // this.data.to.value = null;
    this.data.to.externalState = null;
    this.data.to.lockState = OENLockedState.UNLOCKED;
    this.suspendToControl.resetInputControl();
    this.updateLocking();
  }

  /**********************************************************************/
  /* LOCKING */

  // Lock All
  public lockInputControl(): void {
    this.data.lockState = OENLockedState.LOCKED;
    this.updateLocking();
  }

  public unlockInputControl(): void {
    this.data.lockState = OENLockedState.UNLOCKED;
    this.updateLocking();
  }

  // From
  public lockFromInputControl(): void {
    this.data.from.lockState = OENLockedState.LOCKED;
    this.updateLocking();
  }

  public unlockFromInputControl(): void {
    this.data.from.lockState = OENLockedState.UNLOCKED;
    this.updateLocking();
  }

  // To
  public lockToInputControl(): void {
    this.data.to.lockState = OENLockedState.LOCKED;
    this.updateLocking();
  }

  public unlockToInputControl(): void {
    this.data.to.lockState = OENLockedState.UNLOCKED;
    this.updateLocking();
  }

  // Checkbox
  public lockCheckboxInputControl(): void {
    this.data.checkbox.lockState = OENLockedState.LOCKED;
    this.updateLocking();
  }

  public unlockCheckboxInputControl(): void {
    this.data.checkbox.lockState = OENLockedState.UNLOCKED;
    this.updateLocking();
  }

  private updateLocking(): void {
    // apply from locking
    if (this.data.lockState === OENLockedState.UNLOCKED) {
      this.data.locked = false;
    } else if (this.data.lockState === OENLockedState.LOCKED) {
      this.data.locked = true;
    }
    // apply from locking
    if (
      this.data.lockState === OENLockedState.UNLOCKED &&
      this.data.from.lockState === OENLockedState.UNLOCKED
    ) {
      if (this.suspendFromControl != null) this.suspendFromControl.unlockInputControl();
    } else if (
      this.data.lockState === OENLockedState.LOCKED ||
      this.data.from.lockState === OENLockedState.LOCKED
    ) {
      if (this.suspendFromControl != null) this.suspendFromControl.lockInputControl();
    }

    // apply to locking
    if (
      this.data.lockState === OENLockedState.UNLOCKED &&
      this.data.to.lockState === OENLockedState.UNLOCKED
    ) {
      if (this.suspendToControl != null) this.suspendToControl.unlockInputControl();
    } else if (
      this.data.lockState === OENLockedState.LOCKED ||
      this.data.to.lockState === OENLockedState.LOCKED
    ) {
      if (this.suspendToControl != null) this.suspendToControl.lockInputControl();
    }

    // apply checkbox locking
    if (
      this.data.lockState === OENLockedState.UNLOCKED &&
      this.data.checkbox.lockState === OENLockedState.UNLOCKED
    ) {
      this.data.checkbox.locked = false;
    } else if (
      this.data.lockState === OENLockedState.LOCKED ||
      this.data.checkbox.lockState === OENLockedState.LOCKED
    ) {
      this.data.checkbox.locked = true;
    }

    this.cd.markForCheck();
  }
}
